import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { ArrowRight, Loader2 } from "lucide-react";

/* =============================================================================
   PLUTO — Login page. Iter 45.38 — rethemed to match Home.js (cream bg,
   deep indigo ink, notebook / doodled accents) with a cursor-following
   moving gradient (rAF-lerped for smoothness).  Every existing
   data-testid is preserved so the auth flow stays testable end-to-end.
============================================================================= */
const CREAM       = "#FBF6E9";
const INK         = "#1E1B4B";
const INK_SOFT    = "#4C4A73";
const INDIGO      = "#4338CA";
const INDIGO_DEEP = "#312E81";

/* Tiny inline doodles — kept local so Login.js doesn't cross-import from Home.js. */
function SquiggleUnderline({ className = "", color = INDIGO }) {
  return (
    <svg viewBox="0 0 300 24" className={className} preserveAspectRatio="none" aria-hidden>
      <path d="M4 15 C 40 4, 80 22, 120 12 S 200 4, 240 15 S 296 8, 296 12"
            stroke={color} strokeWidth="4" strokeLinecap="round" fill="none" />
    </svg>
  );
}
function StarDoodle({ className = "", color = INDIGO }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden fill={color}>
      <path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z" />
    </svg>
  );
}

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  /* Cursor-follow gradient — target position captured on mousemove, actual
     rendered position lerps toward it every frame (~10 % / frame).  The
     result is a warm, breathing spotlight that trails the cursor instead
     of snapping to it. */
  const wrapRef = useRef(null);
  useEffect(() => {
    let rafId;
    let targetX = window.innerWidth / 2;
    let targetY = window.innerHeight / 2;
    let currentX = targetX;
    let currentY = targetY;
    const onMove = (e) => { targetX = e.clientX; targetY = e.clientY; };
    const tick = () => {
      currentX += (targetX - currentX) * 0.10;
      currentY += (targetY - currentY) * 0.10;
      if (wrapRef.current) {
        wrapRef.current.style.setProperty("--mx", `${currentX}px`);
        wrapRef.current.style.setProperty("--my", `${currentY}px`);
      }
      rafId = requestAnimationFrame(tick);
    };
    window.addEventListener("mousemove", onMove);
    rafId = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/dash");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={wrapRef}
      data-testid="login-page"
      className="relative min-h-screen overflow-hidden flex items-center justify-center px-6 py-12"
      style={{ background: CREAM, color: INK, "--mx": "50%", "--my": "50%" }}
    >
      {/* Paper dot texture (same as Home.js) */}
      <div aria-hidden
           className="pointer-events-none absolute inset-0 z-0 opacity-[0.35] mix-blend-multiply"
           style={{
             backgroundImage: "radial-gradient(rgba(30,27,75,0.08) 1px, transparent 1px)",
             backgroundSize: "3px 3px",
           }} />

      {/* Static aurora orbs — same recipe as Home hero */}
      <div aria-hidden className="pointer-events-none absolute -top-40 -left-32 h-[26rem] w-[26rem] rounded-full opacity-55 blur-[110px]"
           style={{ background: "linear-gradient(135deg, #C7D2FE 0%, #A5B4FC 100%)" }} />
      <div aria-hidden className="pointer-events-none absolute -bottom-40 -right-40 h-[32rem] w-[32rem] rounded-full opacity-45 blur-[130px]"
           style={{ background: "linear-gradient(135deg, #DDD6FE 0%, #C4B5FD 100%)" }} />
      <div aria-hidden className="pointer-events-none absolute top-1/2 right-1/3 h-72 w-72 rounded-full opacity-35 blur-[100px] -translate-y-1/2"
           style={{ background: "linear-gradient(135deg, #FBCFE8 0%, #F5D0FE 100%)" }} />

      {/* Cursor-follow spotlight #1 — deep indigo, multiply blend for depth */}
      <div aria-hidden
           className="pointer-events-none absolute inset-0 z-0"
           style={{
             background: "radial-gradient(700px circle at var(--mx) var(--my), rgba(139,92,246,0.22) 0%, rgba(139,92,246,0.10) 30%, transparent 60%)",
             mixBlendMode: "multiply",
           }} />
      {/* Cursor-follow spotlight #2 — warm amber, screen blend for glow */}
      <div aria-hidden
           className="pointer-events-none absolute inset-0 z-0"
           style={{
             background: "radial-gradient(420px circle at var(--mx) var(--my), rgba(255,215,140,0.40) 0%, transparent 55%)",
             mixBlendMode: "screen",
           }} />

      {/* Notebook rules — very faint */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-full opacity-[0.06]"
           style={{ backgroundImage: "repeating-linear-gradient(to bottom, transparent 0, transparent 39px, #1E1B4B 39px, #1E1B4B 40px)" }} />

      {/* --------------------------------- Content -------------------------------- */}
      <div className="relative z-10 w-full max-w-md">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-10 justify-center">
          <div className="h-12 w-12 rounded-xl bg-white flex items-center justify-center border-2 shadow-[0_3px_0_0_#1E1B4B]"
               style={{ borderColor: INK }}>
            <img src="/logo/pluto-logo.png" alt="PLUTO"
                 className="h-full w-full object-contain p-1.5"
                 data-testid="brand-icon-login" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-brand tracking-brand text-2xl" style={{ color: INK }}>PLUTO</span>
            <span className="text-[10px] font-medium tracking-[0.24em] uppercase mt-1"
                  style={{ color: INK_SOFT }}>by CreateX</span>
          </div>
        </div>

        {/* Headline */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <StarDoodle className="h-3 w-3" color={INDIGO} />
            <span className="text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: INDIGO }}>
              Welcome back
            </span>
            <StarDoodle className="h-3 w-3" color={INDIGO} />
          </div>
          <h1 className="font-display font-bold text-4xl md:text-5xl tracking-[-0.02em] leading-[1.05]"
              style={{ color: INK }}>
            Sign in to{" "}
            <span className="relative inline-block whitespace-nowrap">
              <span
                style={{
                  backgroundImage: "linear-gradient(135deg, #4338CA 0%, #7C3AED 50%, #C026D3 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                PLUTO
              </span>
              <SquiggleUnderline className="absolute left-0 -bottom-2 w-full h-3" color={INDIGO} />
            </span>
          </h1>
          <p className="mt-5 font-hand text-xl md:text-2xl" style={{ color: INDIGO_DEEP }}>
            — pick up right where you left off.
          </p>
        </div>

        {/* Form card — notebook-style: white card + dark border + dropped shadow */}
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="relative rounded-2xl bg-white/85 backdrop-blur-md border-2 p-6 md:p-8 space-y-5 shadow-[0_6px_0_0_#1E1B4B]"
          style={{ borderColor: INK }}
        >
          <div className="space-y-2">
            <Label htmlFor="email"
                   className="text-[11px] font-semibold uppercase tracking-[0.14em]"
                   style={{ color: INK }}>
              Work email
            </Label>
            <Input
              id="email"
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@yourteam.com"
              className="h-11 bg-white border-2 rounded-lg focus-visible:ring-0 focus-visible:border-indigo-600 transition-colors"
              style={{ borderColor: "#D6D3E7" }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password"
                   className="text-[11px] font-semibold uppercase tracking-[0.14em]"
                   style={{ color: INK }}>
              Password
            </Label>
            <Input
              id="password"
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="h-11 bg-white border-2 rounded-lg focus-visible:ring-0 focus-visible:border-indigo-600 transition-colors"
              style={{ borderColor: "#D6D3E7" }}
            />
          </div>

          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold text-white transition-all hover:-translate-y-0.5 shadow-[0_5px_0_0_#1E1B4B] hover:shadow-[0_7px_0_0_#1E1B4B] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            style={{ background: "linear-gradient(135deg, #4338CA 0%, #6D28D9 55%, #A21CAF 100%)" }}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>

        {/* Micro copy */}
        <p className="text-center mt-6 text-xs" style={{ color: INK_SOFT }}>
          New here?{" "}
          <Link to="/" className="font-semibold underline decoration-2 underline-offset-2"
                style={{ color: INDIGO_DEEP }}
                data-testid="login-take-a-tour">
            Take a tour first
          </Link>
        </p>
      </div>
    </div>
  );
}
